import { Navigate, Outlet, useParams, useRoutes } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import AppLayout from '@/components/ui/AppLayout';
import NotFound from '@/pages/NotFound';
import Index from '@/pages/Index';
import AuthPage from '@/pages/auth';
import { CalendarPage } from '@/pages/calendar/CalendarPage';
import PrivateRoute from '@/router/PrivateRoute';
import { buildCalendarPath, isViewKind } from '@/lib/calendarRoute';

/**
 * Redirects bare / partial calendar URLs to today's canonical deep link,
 * e.g. /calendar → /calendar/r/month/<today>. Honors an explicit view when one
 * is present (e.g. /calendar/r/week → today's week).
 */
const CalendarTodayRedirect = () => {
  const { view } = useParams();
  const resolvedView = isViewKind(view) ? view : 'month';
  return <Navigate to={buildCalendarPath(resolvedView, new Date())} replace />;
};

const publicRouteChildren: RouteObject[] = [
  { index: true, element: <Index /> },
  { path: 'auth', element: <AuthPage /> },
];

const privateRouteChildren: RouteObject[] = [
  {
    path: 'calendar',
    element: (
      <PrivateRoute>
        <Outlet />
      </PrivateRoute>
    ),
    children: [
      { index: true, element: <CalendarTodayRedirect /> },
      { path: 'r', element: <CalendarTodayRedirect /> },
      { path: 'r/:view', element: <CalendarTodayRedirect /> },
      { path: 'r/:view/:year/:month', element: <CalendarPage /> },
      { path: 'r/:view/:year/:month/:day', element: <CalendarPage /> },
    ],
  },
];

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [...publicRouteChildren, ...privateRouteChildren],
  },
  { path: '*', element: <NotFound /> },
];

const AppRoutes = () => useRoutes(routes);

export default AppRoutes;
